import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState, Platform } from 'react-native';
import { AppSnackbar } from '../services/AppSnackbar';
import {
  ensureBluetoothPermissions,
  hasBluetoothPermissions,
} from '../utils/bluetoothPermissions';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import {
  addPrinterProfiles,
  selectEnabledPrinterProfiles,
  selectPrinterProfiles,
  selectPrinterSettings,
  type PrinterProfile,
  type SettingsSliceObject,
} from '../redux/SettingsSlice';
import { ExtrasContext } from './ExtrasContext';
import { RootState } from '../redux/types';
import {
  DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE,
  getThermalPrintErrorMessage,
  printOrderThermalReceipt,
} from '../services/thermalReceiptService';
import {
  autoPopulateBleProfiles,
  profileToTarget,
  profileToLayoutOpts,
} from '../utils/printerSettingsPayload';
import {
  markPrinterProfileChecking,
  markPrinterProfilePrintFailure,
  markPrinterProfilePrintSuccess,
} from '../services/printerProfileStatusService';
import { isElectronRuntime } from '../utils/isElectron';
import { shareReceiptAsPDF } from '../services/receiptShareService';
import { reportReceiptIssued } from '../services/receiptPrintReport';
import { warmupNativeBleSession } from '../services/nativeBlePrinterSession';
import { getBondedBleDevices } from '../services/thermalPrintCore';
import type { ReceiptTotalsMode } from '../services/receiptTotals';
import appAnalytics from '../services/appAnalytics';
import { buildAppAnalyticsContext } from '../services/appAnalyticsContext';
import type {
  ReceiptPrintErrorClass,
  ReceiptPrintSource,
} from '../services/appAnalyticsTypes';

function classifyPrintError(err: unknown): ReceiptPrintErrorClass {
  const text = (
    err instanceof Error ? err.message : String(err ?? '')
  ).toLowerCase();
  if (text.includes('not enabled')) return 'bluetooth_off';
  if (text.includes('can not find the specified printing device')) {
    return 'not_paired';
  }
  if (text.includes('permission')) return 'permission';
  if (text.includes('timed out') || text.includes('timeout')) return 'timeout';
  if (
    text.includes('no bluetooth printers selected') ||
    text.includes('no printer')
  ) {
    return 'no_printer';
  }
  if (text.includes('read failed') || text.includes('connect')) {
    return 'connect_failed';
  }
  if (text.includes('outputstream') || text.includes('failed to print data')) {
    return 'write_failed';
  }
  return 'unknown';
}

function firstNativeBleMac(
  profiles: PrinterProfile[],
  legacy: SettingsSliceObject['settings']['printer'] | undefined,
): string {
  for (const profile of profiles) {
    if (profile.mode !== 'bluetooth') continue;
    const mac = String(profile.bluetoothAddress || '').trim();
    if (mac) return mac;
  }
  if (legacy?.mode === 'bluetooth') {
    return String(
      legacy.bluetoothAddresses?.[0] || legacy.bluetoothAddress || '',
    ).trim();
  }
  return '';
}

interface PrintReceiptContextType {
  btprint: (
    totalsMode?: ReceiptTotalsMode,
    source?: ReceiptPrintSource,
  ) => Promise<void>;
  shareReceipt: (totalsMode?: ReceiptTotalsMode) => Promise<void>;
  isPrinting: boolean;
}

export const PrintReceiptContext = createContext<PrintReceiptContextType>({
  btprint: async () => {},
  shareReceipt: async () => {},
  isPrinting: false,
});

interface PrintReceiptContextProviderObject {
  children: ReactNode;
}

export const PrintReceiptContextProvider: React.FC<
  PrintReceiptContextProviderObject
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const profiles = useAppSelector(selectEnabledPrinterProfiles);
  const allProfiles = useAppSelector(selectPrinterProfiles);
  const legacyPrinter = useAppSelector(selectPrinterSettings);
  const { receipt } = useContext(ExtrasContext);
  const business = useAppSelector(
    (state: RootState) => state.auth.inApp.account.businessInfo,
  );
  const employeedata = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const currency = useAppSelector(
    (state: RootState) =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currentLocation = useAppSelector(
    (state: RootState) => state.location.currentLocation,
  );
  const analyticsContext = () =>
    buildAppAnalyticsContext({
      role: employeedata?.bp_employee_role,
      locationType: currentLocation?.bp_location_is_system_generated
        ? 'test'
        : currentLocation
          ? 'real'
          : undefined,
    });
  const printInFlightRef = useRef(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const nativeBleMac = firstNativeBleMac(profiles, legacyPrinter);

  /**
   * Same bonded-device auto-populate the printer settings screens run on
   * focus, moved to app open / resume. Without this the profile list is empty
   * after a fresh install or logout until someone visits settings, and the
   * warmup below has no MAC to connect to.
   */
  useEffect(() => {
    if (isElectronRuntime()) return;
    if (Platform.OS !== 'android' && Platform.OS !== 'ios') return;

    const populate = async () => {
      try {
        // getBondedBleDevices() closes the shared native socket when it
        // returns, so only read the bond list while no Bluetooth profile
        // exists yet. Once one is saved, the warm session must stay open.
        if (allProfiles.some(p => p.mode === 'bluetooth')) return;
        if (!(await hasBluetoothPermissions())) return;
        const bonded = await getBondedBleDevices();
        const newProfiles = autoPopulateBleProfiles(bonded, allProfiles);
        if (newProfiles.length) dispatch(addPrinterProfiles(newProfiles));
      } catch {
        // Adapter off or permission missing: nothing to populate
      }
    };

    void populate();
    const sub = AppState.addEventListener('change', next => {
      if (next === 'active') void populate();
    });
    return () => sub.remove();
  }, [allProfiles, dispatch]);

  useEffect(() => {
    if (isElectronRuntime()) return;
    if (!nativeBleMac) return;

    void warmupNativeBleSession(nativeBleMac);

    const sub = AppState.addEventListener('change', next => {
      if (next === 'active') void warmupNativeBleSession(nativeBleMac);
    });
    return () => sub.remove();
  }, [nativeBleMac]);

  const btprint = async (
    totalsMode: ReceiptTotalsMode = 'print',
    source: ReceiptPrintSource = 'unknown',
  ): Promise<void> => {
    if (printInFlightRef.current) return;
    printInFlightRef.current = true;
    setIsPrinting(true);
    const tapProps = {
      ...analyticsContext(),
      source,
      totals_mode: totalsMode,
      printer_count: profiles.length,
    };
    appAnalytics.track('receipt_print_tapped', {
      ...tapProps,
      printer_mode: profiles[0]?.mode ?? legacyPrinter?.mode ?? 'none',
      profile_id: profiles[0]?.id ?? 'legacy',
    });
    try {
      // Fall back to legacy single-printer path if no profiles configured
      if (!profiles.length) {
        const startedAt = Date.now();
        const legacyProps = {
          ...tapProps,
          printer_mode: legacyPrinter?.mode ?? 'none',
          profile_id: 'legacy',
        };
        const result = await legacyPrint(totalsMode);
        if (result.ok) {
          appAnalytics.track('receipt_print_succeeded', {
            ...legacyProps,
            duration_ms: Date.now() - startedAt,
          });
          reportReceiptIssued(receipt?.orderID, employeedata?.bp_employee_role);
          AppSnackbar.show('Receipt sent to printer', 'success');
        } else {
          appAnalytics.track('receipt_print_failed', {
            ...legacyProps,
            duration_ms: Date.now() - startedAt,
            error_class: classifyPrintError(result.err),
            error_message: getThermalPrintErrorMessage(result.err),
          });
        }
        return;
      }

      // Request BLE permissions once if any profile needs Bluetooth
      // Skip on Electron — Web Bluetooth handles permissions via the device picker
      if (profiles.some(p => p.mode === 'bluetooth') && !isElectronRuntime()) {
        const hasBt = await ensureBluetoothPermissions();
        if (!hasBt) {
          AppSnackbar.show(
            'Bluetooth permission is required to print.',
            'error',
          );
          return;
        }
      }
      const payloadArgs = {
        receipt,
        business,
        employeedata,
        currency,
        totalsMode,
        logoUrl: currentLocation?.bp_location_logo,
      };
      let ok = 0;
      const failed: Array<{ label: string; err: unknown }> = [];

      for (const profile of profiles) {
        const startedAt = Date.now();
        const profileProps = {
          ...tapProps,
          printer_mode: profile.mode,
          profile_id: profile.id,
        };
        try {
          markPrinterProfileChecking(dispatch, profile.id);
          const target = profileToTarget(profile);
          const layoutOpts = profileToLayoutOpts(profile);
          await printOrderThermalReceipt(payloadArgs, layoutOpts, target);
          markPrinterProfilePrintSuccess(dispatch, profile.id, 'print');
          appAnalytics.track('receipt_print_succeeded', {
            ...profileProps,
            duration_ms: Date.now() - startedAt,
          });
          ok++;
        } catch (e) {
          const message = getThermalPrintErrorMessage(e);
          markPrinterProfilePrintFailure(
            dispatch,
            profile.id,
            'print',
            message,
          );
          appAnalytics.track('receipt_print_failed', {
            ...profileProps,
            duration_ms: Date.now() - startedAt,
            error_class: classifyPrintError(e),
            error_message: message,
          });
          failed.push({ label: profile.label, err: e });
          console.log(`Print failed for "${profile.label}":`, e);
        }
      }

      // The server learns who issued the receipt once it is on paper.
      if (ok > 0) {
        reportReceiptIssued(receipt?.orderID, employeedata?.bp_employee_role);
      }

      if (ok === 0) {
        const firstError = failed[0]?.err;
        AppSnackbar.show(getThermalPrintErrorMessage(firstError), 'error', {
          durationMs: 7500,
        });
      } else if (failed.length) {
        const failedNames = failed.map(f => f.label).join(', ');
        AppSnackbar.show(
          `Printed to ${ok}/${profiles.length}. Failed: ${failedNames}`,
          'warning',
          { durationMs: 7500 },
        );
      } else {
        AppSnackbar.show('Receipt sent to printer', 'success');
      }
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
    }
  };

  /**
   * Legacy single-printer path — used when no printerProfiles exist.
   * Preserves exact behavior for users who haven't migrated.
   */
  type LegacyPrintResult = { ok: true } | { ok: false; err: unknown };
  const legacyPrint = async (
    totalsMode: ReceiptTotalsMode = 'print',
  ): Promise<LegacyPrintResult> => {
    const printer = legacyPrinter;
    const charsPerLine = printer?.charsPerLine ?? 32;
    const widthMM = printer?.widthMM ?? 48;
    const autoCut = printer?.autoCut ?? false;
    const timeoutMs = printer?.timeoutMs ?? 30000;
    const ip = printer?.ip ?? '192.168.100.246';
    const port = printer?.port ?? 9100;

    const payloadArgs = {
      receipt,
      business,
      employeedata,
      currency,
      totalsMode,
      logoUrl: currentLocation?.bp_location_logo,
    };
    const layoutOpts = { charsPerLine, autoCut, widthMM };

    if (Platform.OS === 'web') {
      try {
        if (printer?.mode === 'network') {
          const name = (printer?.printerName || '').trim();
          if (!name) {
            AppSnackbar.show(
              'No printer selected. Go to Printer Settings and select a printer.',
              'warning',
              { durationMs: 7500 },
            );
            return { ok: false, err: new Error('No printer selected') };
          }
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'network',
            printerName: name,
          });
          return { ok: true };
        }

        if (printer?.mode === 'usb') {
          const vid = (printer?.usbVendorId || '').trim();
          const pid = (printer?.usbProductId || '').trim();
          if (!vid || !pid) {
            AppSnackbar.show(
              'No printer saved. Go to Printer Settings, scan a printer, then test or save the printer.',
              'warning',
              { durationMs: 7500 },
            );
            return { ok: false, err: new Error('No printer saved') };
          }
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'usb',
            vendorId: vid,
            productId: pid,
          });
          return { ok: true };
        }

        if (printer?.mode === 'web-bluetooth') {
          const profile = DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE;
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'web-bluetooth' as const,
            bleDeviceId: (printer?.bleDeviceId || '').trim() || undefined,
            bleDeviceName: (printer?.bleDeviceName || '').trim() || undefined,
            bleServiceUuid:
              (printer?.bleServiceUuid || '').trim() || profile.bleServiceUuid,
            bleCharacteristicUuid:
              (printer?.bleCharacteristicUuid || '').trim() ||
              profile.bleCharacteristicUuid,
            bleNamePrefix:
              (printer?.bleNamePrefix || '').trim() ||
              (printer?.bleDeviceName || '').trim() ||
              undefined,
          });
          return { ok: true };
        }

        if (printer?.mode === 'web-serial') {
          const baudRate = Number(printer?.serialBaudRate) || 38400;
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'web-serial',
            serialBaudRate: baudRate,
            serialDataBits: printer?.serialDataBits,
            serialStopBits: printer?.serialStopBits,
            serialParity: printer?.serialParity,
            serialFlowControl: printer?.serialFlowControl,
            serialBufferSize: printer?.serialBufferSize,
            serialUsbVendorId: printer?.serialUsbVendorId,
            serialUsbProductId: printer?.serialUsbProductId,
          });
          return { ok: true };
        }

        AppSnackbar.show(
          'No printer configured. Choose a printer in Printer Settings first.',
          'warning',
          { durationMs: 7500 },
        );
        return { ok: false, err: new Error('No printer configured') };
      } catch (err) {
        console.log('Web printing failed:', err);
        AppSnackbar.show(getThermalPrintErrorMessage(err), 'error', {
          durationMs: 7500,
        });
        return { ok: false, err };
      }
    }

    const printViaNet = async (): Promise<void> => {
      await printOrderThermalReceipt(payloadArgs, layoutOpts, {
        mode: 'tcp',
        host: ip,
        port,
        timeoutMs,
      });
    };

    const printViaBle = async (): Promise<void> => {
      const hasBt = await ensureBluetoothPermissions();
      if (!hasBt) {
        AppSnackbar.show(
          'Bluetooth not available, trying network printer…',
          'warning',
          { durationMs: 2000 },
        );
        await printViaNet();
        return;
      }

      const macs = (
        printer?.bluetoothAddresses?.length
          ? printer.bluetoothAddresses
          : (printer?.bluetoothAddress || '').trim()
            ? [String(printer?.bluetoothAddress || '').trim()]
            : []
      )
        .map(s => String(s || '').trim())
        .filter(Boolean);

      let targets = macs;
      if (!targets.length) {
        throw new Error('No Bluetooth printers selected');
      }

      let bleOk = 0;
      const bleFailed: Array<{ mac: string; err: unknown }> = [];
      for (const mac of targets) {
        try {
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'bluetooth',
            macAddress: mac,
          });
          bleOk++;
        } catch (e) {
          bleFailed.push({ mac, err: e });
        }
      }

      if (bleOk === 0) {
        throw new Error('Bluetooth printing failed');
      }
      if (bleFailed.length) {
        console.log('Some BLE printers failed:', bleFailed);
        AppSnackbar.show(
          `Printed to ${bleOk}/${targets.length} Bluetooth printer(s).`,
          'warning',
          { durationMs: 2500 },
        );
      }
    };

    const printViaUsb = async (): Promise<void> => {
      if (Platform.OS !== 'android') {
        throw new Error('USB printing is only supported on Android');
      }
      const vid = (printer?.usbVendorId || '').trim();
      const pid = (printer?.usbProductId || '').trim();
      if (!vid || !pid) {
        throw new Error('Select a USB printer in Printer Settings');
      }
      await printOrderThermalReceipt(payloadArgs, layoutOpts, {
        mode: 'usb',
        vendorId: vid,
        productId: pid,
      });
    };

    try {
      if (printer?.mode === 'usb') {
        await printViaUsb();
        return { ok: true };
      }
      if (printer?.mode === 'tcp') {
        await printViaNet();
        return { ok: true };
      }

      try {
        await printViaBle();
      } catch (bleErr) {
        console.log('Bluetooth printing failed, trying network:', bleErr);
        AppSnackbar.show(
          'Bluetooth print failed, trying network printer…',
          'warning',
          { durationMs: 2500 },
        );
        await printViaNet();
      }
      return { ok: true };
    } catch (err) {
      console.log('Printing failed:', err);
      AppSnackbar.show(getThermalPrintErrorMessage(err), 'error', {
        durationMs: 7500,
      });
      return { ok: false, err };
    }
  };

  const shareReceipt = async (
    totalsMode: ReceiptTotalsMode = 'print',
  ): Promise<void> => {
    try {
      await shareReceiptAsPDF({
        receipt,
        business,
        employeedata,
        currency,
        totalsMode,
        logoUrl: currentLocation?.bp_location_logo,
      });
      reportReceiptIssued(receipt?.orderID, employeedata?.bp_employee_role);
    } catch (err: any) {
      // User cancelled the share sheet — not an error
      if (
        err?.message?.includes('User did not share') ||
        err?.message?.includes('CANCELED')
      ) {
        return;
      }
      console.log('Share receipt failed:', err);
      AppSnackbar.show('Failed to share receipt. Please try again.', 'error', {
        durationMs: 7500,
      });
    }
  };

  const value = useMemo(
    () => ({ btprint, shareReceipt, isPrinting }),
    [btprint, shareReceipt, isPrinting],
  );

  return (
    <PrintReceiptContext.Provider value={value}>
      {children}
    </PrintReceiptContext.Provider>
  );
};
